import { MetadataClient } from '@mae/client-web/metadata-client';
import { themeSettingsStore } from '@mae/components/themes/theme-settings-store';
import ParseColor from 'parse-color';

const prefix = '--hxg-';
const contrastThreshold = 160;
export type storageKey = 'primaryColor' | 'secondaryColor' | 'isDefault';
export type rootKey = 'primary' | 'secondary';

/**
 * Core branding utility functions that can be used independently of storage mechanism.
 * These are pure functions that operate on CSS variables and colors.
 */
export abstract class BrandingCore {
	static getRootVariable = (name: rootKey, doc: Document = document): string => {
		function getValue(k: string) {
			return getComputedStyle(doc.documentElement).getPropertyValue(k).trim();
		}
		const key = `${prefix}${name}`;

		const storedHex = getValue(`${key}-hex`);
		if (storedHex) {
			return storedHex;
		}

		const hsl = {
			h: getValue(`${key}-h`),
			s: getValue(`${key}-s`),
			l: getValue(`${key}-l`)
		};

		const parsed = ParseColor(`hsl(${hsl.h}, ${hsl.s}, ${hsl.l})`);

		return parsed.hex ?? '';
	};

	static setRootVariable = (name: rootKey, value: string, doc: Document = document): void => {
		function setValue(k: string, v: string) {
			return doc.documentElement.style.setProperty(k, v);
		}

		const parsed = ParseColor(value);
		const key = `${prefix}${name}`;

		if (parsed.hsl) {
			setValue(`${key}-hex`, parsed.hex);
			setValue(`${key}-h`, parsed.hsl[0].toString());
			setValue(`${key}-s`, parsed.hsl[1] + '%');
			setValue(`${key}-l`, parsed.hsl[2] + '%');
		}
	};

	static setRootHighContrastVariables = (doc: Document = document): void => {
		function setValue(k: string, v: string) {
			return doc.documentElement.style.setProperty(k, v);
		}

		const primary: Array<number> = ParseColor(BrandingCore.getRootVariable('primary', doc)).rgb;
		const secondary: Array<number> = ParseColor(BrandingCore.getRootVariable('secondary', doc)).rgb;
		let highContrast: { main: string; bland: string };

		if (primary) {
			highContrast = BrandingCore.getHighContrastColors(primary[0], primary[1], primary[2]);
			setValue(`--hxg-primary-highcontrast`, highContrast.main);
			setValue(`--hxg-primary-highcontrast-bland`, highContrast.bland);
		}

		if (secondary) {
			highContrast = BrandingCore.getHighContrastColors(secondary[0], secondary[1], secondary[2]);
			setValue(`--hxg-secondary-highcontrast`, highContrast.main);
			setValue(`--hxg-secondary-highcontrast-bland`, highContrast.bland);
		}
	};

	static getHighContrastColors = (r: number, g: number, b: number): { main: string; bland: string } => {
		const brightness = (r * 299 + g * 587 + b * 114) / 1000;

		return brightness > contrastThreshold ? { main: '#000000', bland: '#505050' } : { main: '#ffffff', bland: '#e4e4e4' };
	};

	static isDark = (r: number, g: number, b: number): boolean => {
		return (0.299 * r + 0.587 * g + 0.114 * b) / 255 < 0.3;
	};

	static isValidColor = (input: string): boolean => {
		const color = ParseColor(input.padEnd(7));
		return color.hsl !== undefined;
	};
}

/**
 * Branding class that uses MetadataClient.storageId for storage.
 * This is the main Branding class used by most applications.
 */
export abstract class Branding extends BrandingCore {
	static updateBranding = (primary: string, secondary: string, isDefault: boolean): void => {
		Branding.setLocalStorage('primaryColor', primary);
		Branding.setLocalStorage('secondaryColor', secondary);
		Branding.setLocalStorage('isDefault', isDefault.toString());

		Branding.setBrandingColors(primary, secondary);
	};

	static getLocalStorage = (name: storageKey): string => {
		const lsKey = MetadataClient.storageId;
		return localStorage.getItem(lsKey) ? JSON.parse(localStorage.getItem(lsKey))[name] : undefined;
	};

	static setLocalStorage = (name: storageKey, value: string): void => {
		const lsKey = MetadataClient.storageId;
		const storage = JSON.parse(localStorage.getItem(lsKey));
		storage[name] = value;
		localStorage.setItem(lsKey, JSON.stringify(storage));
	};

	static setBrandingColors = (
		primary = Branding.getLocalStorage('primaryColor'),
		secondary = Branding.getLocalStorage('secondaryColor'),
		doc = document
	): void => {
		Branding.setRootVariable('primary', primary, doc);
		Branding.setRootVariable('secondary', secondary, doc);
		Branding.setRootHighContrastVariables(doc);

		themeSettingsStore.setPrimaryColor(primary);
		themeSettingsStore.setSecondaryColor(secondary);
	};
}
